/**
 * @module Channels
 */

import { Channel, ChannelRB } from 'rainbow-web-sdk/models/channel.model';
import { Subject, Subscription, filter } from "rxjs";
import { ErrorHelperService, LoggerService, MainService, CHANNEL_SVC, ChannelService } from 'rainbow-web-sdk';
import { CONTACT_SVC, ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { User } from 'rainbow-web-sdk/models/user.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';

/**
 * @internal
 * maximum number of images that can inserted in a post.
 */
export const CHANNEL_POST_MAX_IMAGES = 10;

/**
 * @internal
 * maximum number of files that can attached to a post.
 */
export const CHANNEL_POST_MAX_ATTACHMENTS = 10;

/**
 * @eventProperty
 * Channel post instance specific events.
 * Can listen to it via `ChannelPost.subscribe()` API
 */
export enum ChannelPostEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when the connected user updates its reaction to a channel post .
     */
    ON_CONNECTED_USER_REACTION_UPDATED = "ON_CONNECTED_USER_REACTION_UPDATED",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when the post already published in the channel has been modified.
     */
    ON_POST_UPDATED = "ON_POST_UPDATED",
}

/**
 * Possible format for a post channel
 */
export enum ChannelPostMessageFormat {
    /** Simple text message */
    BASIC = "urn:xmpp:channels:basic",

    /** Message in HTML format*/
    HTML = "urn:xmpp:channels:html",

    /** Message in MARKDOWN format*/
    MARKDOWN = "urn:xmpp:channels:markdown"
}

/**
 * Possible reactions from users on a post channel
 */
export enum ChannelPostReaction {
    /** No reaction */
    NONE = "none",

    /** The traditional Like */
    LIKE = "like",

    /** Fan and happy about the publication */
    LOVE = "happy",

    /** Applause used for example as congratulations */
    APPLAUSE = "applause",

    /** Fantastic, amazing... */
    FANTASTIC = "fantastic",

    /** Intrigued by the publication */
    CURIOUS = "doubt"
}

/**
 * Represents the reaction data for a post.
 * Each property corresponds to a specific reaction type, and its value indicates the number of times that reaction has been expressed.
 */
type ReactionData = Record<ChannelPostReaction, number>;

/**
 * Represents a reaction to a post made by a user.
 */
export interface ChannelPostUserReactions {
    /**
     * The reaction to a post.
     * @readonly
     */
    reaction: ChannelPostReaction;

    /**
     * The user who posted this reaction
     * @readonly
     */
    user: User;
}

/**
 * @internal
 */
export class ChannelPostComment {
    public static create(id: string, from: User, message: string): ChannelPostComment {
        const comment = new ChannelPostComment();
        comment.id = id;
        comment.from = from;
        comment.message = message;
        return comment;
    }

    private constructor() {}
    public id: string;
    public message: string;
    public from?: User;
}

export interface ChannelPostFileDocument {
    /**
     * The unique identifier associated to a channel image or attachment document.
     */
    id: string;
}

export interface ChannelPostVideoDocument {
    /**
     * The unique identifier(ID) associated to a channel video document.
     * This ID is a unique string of characters found in the video's URL used to uniquely identify each video on the platform.
     * This ID allows you to find the video in the YouTube database and access it directly to watch it.
     */
    id: string;

    /**
     * Video provider (e.g. “YouTube” for a Web provider).
     */
    provider: string;
}

/**
 * Interface dedicated to publishing a new post or modifying an existing post.
 * In the case of a new post, this information does not need to be provided.
 * However, if an existing post is being modified, its id must be specified.
 * Only information that can be included in a post for publication is displayed in this interface.
 * Additional information such as that associated with the publisher, reactions... are only available
 * via the 'ChannelPost' interface once the post has been added/modified.
 *
 */
export interface ChannelPostPublication {
    /**
     * The channel post unique identifier.
     */
    id?: string;

    /**
     * The title of the post.
     */
    title?: string;

    /**
     * The format of the message.
     */
    format?: ChannelPostMessageFormat;

    /**
     * The text message included in the post.
     */
    message?: string;

    /**
     * List of images inserted in the post.
     */
    images?: ChannelPostFileDocument[];

    /**
     * List of documents (of any kind) attached in the post.
     */
    attachments?: ChannelPostFileDocument[];

    /**
     * Video attached in the post.
     */
    video?: ChannelPostVideoDocument;
}

export interface ChannelPost {

    // ----------------------------------------------------
    // INTERFACE DATA
    // ----------------------------------------------------

    /**
     * The channel post unique identifier.
     * @readonly
     */
    id: string;

    /**
     * Date when the post was published or modified.
     * @readonly
     */
    timestamp: number;

    /**
     * Indicates whether it is a new post in the channel.
     * This status can be managed by an SDK client to indicate, for example, that the post has been seen or read by a user.
     * @readonly
     */
    isNew: boolean;

    /**
     * Indicates whether the post has been modified after publication.
     * @readonly
     */
    isModified: boolean;

    /**
     * The title of the post.
     */
    title: string;

    /**
     * The format of the message.
     */
    format: ChannelPostMessageFormat;

    /**
     * The text message included in the post.
     */
    message: string;

    /**
     * List of images inserted in the post.
     */
    images: ChannelPostFileDocument[];

    /**
     * Maximum number of images that can be inserted into the post
     */
    readonly maxImages: number;

    /**
     * List of documents (of any kind) attached in the post.
     */
    attachments: ChannelPostFileDocument[];

    /**
     * Maximum number of documents that can be attached in the post
     */
    readonly maxAttachments: number;

    /**
     * Video attached in the post.
     */
    video: ChannelPostVideoDocument;

    /**
     * The reaction of the connected user on the post.
     * Reactions allow the user to express a more precise feeling/emotion about what he think of the post!
     */
    reaction: ChannelPostReaction;

    /**
     * Represents all the reaction(s) on the post.
     * For each reaction type, the number of times that reaction has been expressed is provided
     * but NOT the information about which user.
     * @readonly
     */
    reactionCounters: ReactionData;

    /**
     * The channel in which the post was published.
     * @readonly
     */
    channel: Channel;

    /**
     * The publisher of the post.
     * @readonly
     */
    publisher: User;

    // ----------------------------------------------------
    // INTERFACE API
    // ----------------------------------------------------

    /**
     * Allows ChannelPostEvents events to be sent.
     * Such events can be listened via channelMessage.subscribe() API
     * @param name - the name of the event
     * @param data - optionally, data associated with the event to be sent (as an object)
     */
    sendEvent(name: ChannelPostEvents, data?: any): void;

    /**
     * Subscribe to channel post updates, all events are of RBEvent<ChannelPostEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) A list of event names that the user wants to receive when subscribing (used as filter)
     */
    subscribe(callback: (event: RBEvent<ChannelPostEvents>) => any, eventNames?: ChannelPostEvents[]): Subscription;

    /**
     * Update the reaction of the connected user to the current channel post.
     * This includes adding, updating and removing a reaction.
     * @param reactionType - The user whose role will be changed.
     */
    reactToPost(reactionType: ChannelPostReaction): Promise<void>;

    /**
     * Get all user reactions or a specific reaction to the current message on the channel.
     * @param reaction - Optional filter by reaction type. If no filter is specified, all reactions will be returned.
     */
    getPostUserReactions(reaction?: ChannelPostReaction): Promise<ChannelPostUserReactions[]>;

    /**
     * Delete the channel post.
     * This operation is only authorized the owner of the publication, i.e. the author.
     */
    delete(): Promise<void>;

    /**
     * Method to return a simplified JSON representation of a Channel post object
     */
    toJSON(): any;
}

/**
 * @internal
 */
export class ChannelMessage implements ChannelPost {

    // ----------------------------------------------------
    // CHANNEL POST INSTANCE LIFE CYCLE
    // ----------------------------------------------------
    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private channelService?: any;
    private contactService?: any;

    // ----------------------------------------------------
    // UCaaS + SDK Properties
    // ----------------------------------------------------
    readonly maxImages: number = CHANNEL_POST_MAX_IMAGES;
    readonly maxAttachments: number = CHANNEL_POST_MAX_ATTACHMENTS;

    public id: string = null;
    public format: ChannelPostMessageFormat = null;
    public title: string = null;
    public message: string = null;
    public timestamp: number;

    public images: any[] = null;
    public attachments: any[] = null;
    public video: any = null;

    public isModified: boolean = false;
    public isNew: boolean = true;

    public reaction: ChannelPostReaction;
    public reactionCounters: ReactionData;

    // The initial publisher ID data is retained in addition to the ‘publisher’ data in cases where
    // the latter cannot be retrieved (i.e., no valid instance of User available).
    public publisherId: string = null;
    public publisher: User;

    public channel: ChannelRB;

    // ----------------------------------------------------
    // UCaaS Only: Internal management
    // ----------------------------------------------------
    public type: string = null;
    public timestampDisplay: any;
    public tags: string[] = null; // Proprietary Metadata
    public comments: ChannelPostComment[] = [];

    // Observables for events sent by a channel post (ChannelMessage instance)
    private rxSubject: Subject<RBEvent<ChannelPostEvents>> = new Subject<RBEvent<ChannelPostEvents>>();

    constructor() {
        this.channelService = MainService.getInstance().inject<ChannelService>(CHANNEL_SVC);
        this.contactService = MainService.getInstance().inject<ContactService>(CONTACT_SVC);
    }

    // ============================================================
    //                      OBSERVABLE STUFF
    // ============================================================
    public sendEvent(name: ChannelPostEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create<ChannelPostEvents, any>(name, data));

    }

    public subscribe(callback: (event: RBEvent<ChannelPostEvents>) => any, eventNames?: ChannelPostEvents | ChannelPostEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<ChannelPostEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<ChannelPostEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[ChannelMessage] subscribe () -- error -- ${error.message}`); }
        });
    }

    // ============================================================
    //                  CHANNEL POST MANAGEMENT
    // ============================================================

    public static async createFromData(channel: ChannelRB, data: any): Promise<ChannelMessage> {
        const logger = LoggerService.getInstance();
        const contactService = ContactService.getInstance();

        const newPost = new ChannelMessage();
        newPost.id = data.id;
        newPost.type = data.type;
        if (data.entry.type) {
            newPost.format = data.entry.type;
        }
        newPost.isNew = data.new;
        newPost.isModified = data.modified;
        newPost.publisherId = data.from;
        newPost.publisher = await contactService?.getOrCreateContact(data.from);
        if (newPost.publisher) {
            const nameForLogs = newPost.publisher['nameForLogs'];
            logger.debug(`[ChannelMessage] createFromData -- New post id: ${newPost.id} - from: ${newPost.publisherId} - name: ${nameForLogs}`);
        } else {
            logger.warn(`[ChannelMessage] createFromData -- Unable to retrieve the user associated with the id ${newPost.publisherId}`);
        }
        newPost.timestamp = data.timestamp;
        newPost.timestampDisplay = new Date(data.timestamp);

        newPost.channel = channel;

        if (data.entry.title) { newPost.title = data.entry.title; }
        if (data.entry.message) { newPost.message = data.entry.message.replace(/<a/gi, "<a target='_blank' rel='noopener noreferrer' "); }
        if (data.entry.video) { newPost.video = data.entry.video; }
        if (data.entry.images && data.entry.images !== 'null' && data.entry.images !== undefined) {
            if (data.entry.images instanceof Array) { if (data.entry.images.length > 0) { newPost.images = data.entry.images; } }
            else { newPost.images = [data.entry.images]; }
        }
        if (data.entry.attachments && data.entry.attachments !== 'null' && data.entry.attachments !== undefined) {
            if (data.entry.attachments instanceof Array) { if (data.entry.attachments.length > 0) { newPost.attachments = data.entry.attachments; } }
            else { newPost.attachments = [data.entry.attachments]; }
        }

        newPost.reaction = data.my_appreciation ? data.my_appreciation : ChannelPostReaction.NONE;
        newPost.reactionCounters = data.appreciations ? data.appreciations : { [ChannelPostReaction.LIKE]: 0, [ChannelPostReaction.FANTASTIC]: 0, [ChannelPostReaction.APPLAUSE]: 0, [ChannelPostReaction.CURIOUS]: 0, [ChannelPostReaction.LOVE]: 0 };

        if (data.entry.tag) {
            newPost.tags = Array.from(data.entry.tag);
        }

        return newPost;
    }

    public static updateFromData(channel: ChannelRB, currentPost: any, updatedPost: any): ChannelMessage {
        // const logger = LoggerService.getInstance();

        const newPost = new ChannelMessage();

        // Initialize the new ChannelMessage object with the existing message
        // This avoid to loose additional information when message is updated on new XMPP notification
        // which contain only a subset of these information
        // For example mylike and appreciation counters are not present; 'isNew' is only for internal management ...
        Object.assign(newPost, currentPost);

        // Update new ChannelMessage object with information received in XMPP notification or deduced from them
        newPost.id = updatedPost.id;
        newPost.isModified = updatedPost.modified;
        newPost.type = updatedPost.type;
        newPost.publisherId = updatedPost.from;
        newPost.timestamp = updatedPost.timestamp;
        newPost.timestampDisplay = new Date(updatedPost.timestamp);
        newPost.channel = channel;
        // logger.info(`[ChannelMessage] updateFromData   id:${newPost.id} -- from:${newPost.publisherId} -`);

        // Special reset of elements that can be part of the message  (text/image/attachmens/video) elements
        // If no <message>/<images>/<attachments>/<video> tag is present in received XMPP message
        // it means that no associated element is currently present in the channel message
        // BUT it is also possible that this one has been deleted from the message, hence the reset.
        // If present, these data will be set again.
        newPost.video = null;
        newPost.images = null;
        newPost.attachments = null;
        newPost.message = null;

        if (updatedPost.entry.type) {
            newPost.type = updatedPost.entry.type;
            newPost.format = updatedPost.entry.type;
        }

        if (updatedPost.entry.title) {
            newPost.title = updatedPost.entry.title;
        }

        if (updatedPost.entry.message) {
            newPost.message = updatedPost.entry.message.replace(/<a/gi, "<a target='_blank' rel='noopener noreferrer' ");
        }

        if (updatedPost.entry.video) {
            newPost.video = updatedPost.entry.video;
        }

        if (updatedPost.entry.images && updatedPost.entry.images !== 'null' && updatedPost.entry.images !== undefined) {
            if (updatedPost.entry.images instanceof Array) {
                if (updatedPost.entry.images.length > 0) {
                    newPost.images = updatedPost.entry.images;
                }
            }
            else {
                newPost.images = [updatedPost.entry.images];
            }
        }

        if (updatedPost.entry.attachments && updatedPost.entry.attachments !== 'null' && updatedPost.entry.attachments !== undefined) {
            if (updatedPost.entry.attachments instanceof Array) {
                if (updatedPost.entry.attachments.length > 0) {
                    newPost.attachments = updatedPost.entry.attachments;
                }
            }
            else {
                newPost.attachments = [updatedPost.entry.attachments];
            }
        }

        if (updatedPost.entry.tag) {
            newPost.tags = Array.from(updatedPost.entry.tag);
        }

        return newPost;
    }

    public async delete(): Promise<void> {
        try {
            await this.channelService?.deleteChannelPost(this.channel, this);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'delete');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async reactToPost(reactionType: ChannelPostReaction): Promise<void> {
        try {
            await this.channelService?.reactToPost(this.channel, this, reactionType);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'reactToPost');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async getPostUserReactions(reactionType?: ChannelPostReaction): Promise<ChannelPostUserReactions[]> {
        try {
            return await this.channelService?.getPostUserReactions(this, reactionType);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPostUserReactions');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    // ----------------------------------------------------
    //  CHANNEL POST INSTANCE DEBUG (MAY BE USED BY SDK)
    // ----------------------------------------------------

    public toJSON(): any {
        // JSON output for channel post information
        const json: any = {
            id: this.id,
            timestamp: this.timestamp,
            isNew: this.isNew,
            isModified: this.isModified,
            format: this.format,
            title: this.title,
            message: this.message
        };

        json.images = [];
        this.images?.forEach((image: ChannelPostFileDocument) => {
            json.images.push(image.id);
        });

        json.attachments = [];
        this.attachments?.forEach((attachment: ChannelPostFileDocument) => {
            json.attachments.push(attachment.id);
        });

        if (json.video) {
            json.video = {
                id: this.video.id,
                provider: this.video.provider
            };
        }

        json.myReaction = this.reaction;
        json.allReactions = this.reactionCounters;
        json.channelName = this.channel.name;
        json.publisherId = this.publisherId;
        if (this.publisher?.displayName) {
            json.publisherName = this.publisher.displayName;
        }

        return json;
    }
}
